import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { installerBranding } from "@/branding/Branding";
import InstallerShell from "@/components/installer/InstallerShell";
import KpiCard from "@/components/installer/KpiCard";
import ProjectDesktopCard from "@/components/installer/ProjectDesktopCard";
import TaskWidgets from "@/components/installer/TaskWidgets";
import PortalHero from "@/components/ui/PortalHero";
import { portalIdentity } from "@/lib/portalIdentity";
import {
    Briefcase, ListChecks, CalendarClock, CheckCircle2, ClipboardList,
} from "lucide-react";

/**
 * Engineer Dashboard — engineer-scoped workload view.
 *
 * All KPIs and widgets are scoped to the currently-signed-in engineer
 * (resolved via `listEngineers()` → email match). Source of truth for
 * project assignment is `project.lead_engineer_id` (the DEV-003A field
 * set by `assignLeadEngineer`). Task assignment uses `assigned_engineer_id`.
 * Completed-this-week uses the existing `task.completed_date` timestamp
 * populated by the backend when a task transitions to `Completed`.
 */
export default function Installer() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [workload, setWorkload] = useState(null);
    // DEV-010B-2 · engineer-scoped Site Surveys count for the dashboard
    // KPI tile. Reuses `/api/engineers/me/surveys` (DEV-010B-1) so Web +
    // Mobile share the exact same "needs attention" definition.
    const [surveysNeedingAttention, setSurveysNeedingAttention] = useState(null);

    // DEV-004C canonical Engineer workload contract. A single server call
    // returns engineer_id + all 8 KPI counters (assigned/pending/upcoming/
    // completed-this-week + due-today/overdue/critical-open/completed-today).
    // Both Web and Mobile now consume the same endpoint — no more client-
    // side fan-out, engineer-id resolution or week-boundary math.
    useEffect(() => {
        (async () => {
            try {
                const w = await api.myWorkload();
                setWorkload(w || null);
            } catch { setWorkload(null); }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const rows = await api.listMySurveys();
                const outstanding = (rows || []).filter(
                    (r) => r.status === "not_started" || r.status === "in_progress" || r.status === "returned_for_clarification",
                ).length;
                setSurveysNeedingAttention(outstanding);
            } catch { setSurveysNeedingAttention(null); }
        })();
    }, []);

    const load = async () => {
        try {
            setProjects(await api.listProjects());
        } catch { /* best-effort */ }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const myEngineerId = workload?.engineer_id || null;
    const pendingCount = workload?.pendingTasks ?? null;
    const upcomingVisits = workload?.upcomingVisits ?? null;
    const completedThisWeek = workload?.completedThisWeek ?? null;

    // Engineer's assigned projects — filtered via `lead_engineer_id` for
    // the projects grid render below. The KPI count itself now comes from
    // the workload endpoint (workload.assignedProjects), so the two never
    // drift. `myProjects` remains the source of truth for the card grid.
    //
    // UAT blocker fix · exclude completed projects from the default
    // active view. The backend `assignedProjects` KPI does the same, so
    // KPI + card grid + Mobile "Assigned projects" all agree.
    const myProjects = useMemo(
        () => myEngineerId
            ? projects.filter((p) =>
                p.lead_engineer_id === myEngineerId
                && (p.state || "active") !== "completed"
            )
            : [],
        [projects, myEngineerId],
    );

    return (
        <InstallerShell>
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-6 md:py-8" data-testid="installer-dashboard">
                {/* DEV-022 · Shared premium PortalHero — the Field
                    Portal's strong composition is now the canonical
                    hero used by every role landing (Sales/Ops/Field). */}
                {(() => {
                    const identity = portalIdentity(user?.role || "installer");
                    const sentence = myEngineerId === null ? (
                        <span>Loading your workload…</span>
                    ) : myProjects.length === 0 ? (
                        <span>No projects are currently assigned to you. Speak to Operations if this looks wrong.</span>
                    ) : (
                        <span data-testid="installer-hero-sentence">
                            You have{" "}
                            <Link
                                to={`/installer/tasks?scope=pending&engineer_id=${myEngineerId}`}
                                className="text-white font-semibold underline decoration-white/40 hover:decoration-white transition-colors"
                                data-testid="hero-pending-link"
                            >
                                {pendingCount === null ? "…" : pendingCount} pending {pendingCount === 1 ? "task" : "tasks"}
                            </Link>
                            {" "}and{" "}
                            <Link
                                to={`/installer/upcoming-visits`}
                                className="text-white font-semibold underline decoration-white/40 hover:decoration-white transition-colors"
                                data-testid="hero-visits-link"
                            >
                                {upcomingVisits === null ? "…" : upcomingVisits} upcoming {upcomingVisits === 1 ? "visit" : "visits"}
                            </Link>
                            .
                        </span>
                    );
                    return (
                        <PortalHero
                            testid="installer-hero"
                            eyebrow={`${identity.eyebrow} · YOUR WORKLOAD`}
                            title={user?.name ? `Welcome back, ${user.name.split(" ")[0]}` : "Welcome back"}
                            sentence={sentence}
                            cta={{
                                to: `/installer/calendar?focus=today${myEngineerId ? `&engineer_filter=${myEngineerId}` : ""}`,
                                label: "View today's schedule",
                                icon: CalendarClock,
                            }}
                            ring={{
                                value: upcomingVisits === null ? "—" : upcomingVisits,
                                label: upcomingVisits === 1 ? "upcoming visit" : "upcoming visits",
                            }}
                            tag={`Powered by ${installerBranding.name}`}
                        />
                    );
                })()}
                {/* Keep the legacy anchor so pre-existing tests that
                    query `hero-view-today` continue to resolve; the
                    PortalHero CTA above carries the visible button. */}
                <button
                    onClick={() => navigate(`/installer/calendar?focus=today${myEngineerId ? `&engineer_filter=${myEngineerId}` : ""}`)}
                    data-testid="hero-view-today"
                    aria-hidden
                    tabIndex={-1}
                    className="sr-only"
                >
                    View today's schedule
                </button>

                {/* KPI grid — engineer-scoped */}
                <div className="mt-8 mb-4">
                    <div className="text-xl md:text-2xl text-[#131B2F] font-medium tracking-tight">At a glance</div>
                    <div className="text-sm text-[#4B5565] mt-1">Your workload right now</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-5">
                    <KpiCard
                        testid="kpi-assigned-projects"
                        label="Assigned projects"
                        value={workload?.assignedProjects ?? "—"}
                        helper={workload?.assignedProjects === 1 ? "1 customer" : `${workload?.assignedProjects ?? 0} customers`}
                        icon={Briefcase}
                        variant="brand"
                        onClick={() => navigate("/installer/projects?assigned_to_me=true")}
                        ariaLabel={`View ${workload?.assignedProjects ?? 0} project${workload?.assignedProjects === 1 ? "" : "s"} assigned to you`}
                    />
                    <KpiCard
                        testid="kpi-pending"
                        label="Pending tasks"
                        value={pendingCount ?? "—"}
                        helper={
                            pendingCount === null ? "Loading…" :
                            pendingCount === 0    ? "All caught up" :
                                                     "Assigned to you"
                        }
                        icon={ListChecks}
                        variant="navy"
                        onClick={() => navigate(`/installer/tasks?scope=pending&engineer_id=${myEngineerId || ""}`)}
                        ariaLabel={
                            pendingCount === 0
                                ? "No pending tasks — all caught up"
                                : `View ${pendingCount ?? 0} pending tasks assigned to you`
                        }
                    />
                    <KpiCard
                        testid="kpi-upcoming-visits"
                        label="Upcoming visits"
                        value={upcomingVisits ?? "—"}
                        helper={
                            upcomingVisits === null ? "Loading…" :
                            upcomingVisits === 0    ? "None scheduled" :
                                                      "See your list"
                        }
                        icon={CalendarClock}
                        variant="attention"
                        onClick={() => navigate(`/installer/upcoming-visits`)}
                        ariaLabel={`View ${upcomingVisits ?? 0} upcoming visits assigned to you`}
                    />
                    <KpiCard
                        testid="kpi-completed-this-week"
                        label="Completed this week"
                        value={completedThisWeek ?? "—"}
                        helper={completedThisWeek > 0 ? "Nice work" : "Nothing yet this week"}
                        icon={CheckCircle2}
                        variant="brand"
                        onClick={() => navigate(`/installer/tasks?scope=completed&engineer_id=${myEngineerId || ""}`)}
                        ariaLabel={`View ${completedThisWeek ?? 0} task${completedThisWeek === 1 ? "" : "s"} you completed this week`}
                    />
                    <KpiCard
                        testid="kpi-site-surveys"
                        label="Site surveys"
                        value={surveysNeedingAttention ?? "—"}
                        helper={
                            surveysNeedingAttention === null ? "Loading…" :
                            surveysNeedingAttention === 0    ? "None outstanding" :
                                                                "Needs your attention"
                        }
                        icon={ClipboardList}
                        variant="navy"
                        onClick={() => navigate("/installer/surveys")}
                        ariaLabel={`View ${surveysNeedingAttention ?? 0} site survey${surveysNeedingAttention === 1 ? "" : "s"} assigned to you`}
                    />
                </div>

                {/* Task widgets — engineer-scoped */}
                <TaskWidgets
                    engineerId={myEngineerId}
                    metrics={workload}
                />

                {/* Assigned projects */}
                <div className="mt-10 mb-4">
                    <div className="text-xl md:text-2xl text-[#131B2F] font-medium tracking-tight">Your projects</div>
                    <div className="text-sm text-[#4B5565] mt-1">Projects assigned to you — track progress, stage, and next milestone</div>
                </div>

                {loading || myEngineerId === null ? (
                    <div className="py-16 text-center text-sm text-[#4B5565]">Loading projects…</div>
                ) : myProjects.length === 0 ? (
                    <div className="pe-card p-12 flex flex-col items-center gap-3 text-center" data-testid="engineer-no-projects">
                        <span className="w-14 h-14 rounded-full bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center">
                            <Briefcase size={26} strokeWidth={1.75} />
                        </span>
                        <div className="text-lg text-[#131B2F] font-medium">No projects assigned to you</div>
                        <div className="text-sm text-[#4B5565] max-w-sm">Speak to Operations to have a project assigned.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="installer-projects">
                        {myProjects.map((p) => (
                            <ProjectDesktopCard key={p.id} project={p} testid={`proj-${p.id}`} />
                        ))}
                    </div>
                )}
            </div>
        </InstallerShell>
    );
}

